import { useState, useEffect, useCallback } from 'react';
import { api, MarketAsset } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const KIND_LABEL: Record<string, string> = {
    background: '背景', skin: '内装スキン', avatar: '立ち絵', bgm: 'BGM', se: 'SE',
    brush: 'ブラシ', refpack: '参照パック', script_theme: '台本装丁', stage_preset: '演出プリセット',
};

// マーケット出品の審査（PENDING → 承認=PUBLISHED / 却下=REJECTED）
export function MarketModeration() {
    const [queue, setQueue] = useState<MarketAsset[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setQueue(await api.getMarketQueue());
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const review = async (id: string, action: 'approve' | 'reject') => {
        setBusyId(id);
        const r = await api.reviewMarketAsset(id, action);
        setBusyId(null);
        if (r.success) {
            setQueue(prev => prev.filter(a => a.id !== id));
            toast({ title: action === 'approve' ? '公開しました' : '却下しました' });
        } else if (r.code === 'FORBIDDEN') {
            toast({ title: '管理者権限が必要です', variant: 'destructive' });
        } else {
            toast({ title: '処理に失敗しました', description: r.message, variant: 'destructive' });
        }
    };

    if (loading) return <p className="text-sm text-muted-foreground py-10 text-center">読み込み中…</p>;
    if (queue.length === 0) return <p className="text-sm text-muted-foreground py-10 text-center">審査待ちのアセットはありません。</p>;

    return (
        <div className="space-y-3">
            {queue.map(a => (
                <div key={a.id} className="flex items-center gap-4 rounded-xl border-2 border-ink/10 bg-white p-3">
                    <div
                        className="w-16 h-16 rounded-lg bg-cream-2 shrink-0"
                        style={a.coverUrl ? { backgroundImage: `url(${a.coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-ink line-clamp-1">{a.title}</div>
                        <div className="text-[11px] text-muted-foreground">
                            {KIND_LABEL[a.kind] ?? a.kind} ・ by {a.sellerName ?? '匿名'} ・ ライセンス: {a.license}
                        </div>
                        {a.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{a.description}</div>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button
                            disabled={busyId === a.id}
                            onClick={() => review(a.id, 'approve')}
                            className="rounded-lg bg-emerald-500 text-white text-sm font-bold px-4 py-2 hover:opacity-90 disabled:opacity-50"
                        >公開</button>
                        <button
                            disabled={busyId === a.id}
                            onClick={() => review(a.id, 'reject')}
                            className="rounded-lg border-2 border-red-400 text-red-500 text-sm font-bold px-4 py-2 hover:bg-red-50 disabled:opacity-50"
                        >却下</button>
                    </div>
                </div>
            ))}
        </div>
    );
}
